import { formatToTimeZone, parseFromTimeZone } from '@zenchef/date-fns-timezone'
import { addDays, format, setHours, setMinutes } from 'date-fns'

import { LocaleDate } from '@/utils/translationService'

export function formatTimeString(timestring: string, language: string) {
  if (!timestring) return ''
  const [hours, minutes] = timestring.split(':')
  const date = setMinutes(setHours(new Date(), Number(hours)), Number(minutes))

  if (language === 'en') {
    return format(date, 'h:mm A')
  } else {
    return format(date, 'HH:mm')
  }
}

export function formatTime(date: string | Date | undefined, language: string, timezone: string) {
  if (!date) return ''

  return formatTimeString(
    formatToTimeZone(date, 'HH:mm', {
      timeZone: timezone
    }),
    language
  )
}

export function computeDate(day: string, slot: string, timezone: string) {
  let date = parseFromTimeZone(`${day} ${slot.slice(0, 5)}`, 'YYYY-MM-DD HH:mm', { timeZone: timezone })
  if (slot.endsWith('+1')) {
    date = addDays(date, 1)
  }

  return date
}

/**
 * Extracts the date and slot from a timestamp.
 * @param timestamp The timestamp to extract from.
 * @param restaurantTimezone The timezone of the restaurant.
 * @param locale The locale to use for formatting.
 * @returns An object containing the formatted date and slot. Expected format:
 *          { date: 'YYYY-MM-DD', slot: 'HH:MM' }
 * @throws Will throw an error if the restaurantTimezone is not provided.
 * @example
 * // Example usage:
 * const result = extractDateAndSlotFromTimestamp(1684176600000, 'Europe/Paris', 'en')
 * console.log(result) // { date: '2023-05-15', time: '19:30' }
 */
export const extractDateAndTimeFromTimestamp = (
  timestampInSeconds: number,
  restaurantTimezone?: string,
  locale?: LocaleDate
): { date: string; time: string } => {
  const defaultTimezone = 'UTC'
  const defaultLocale = 'en'

  const timestampMs = timestampInSeconds * 1000
  const formattedDate = formatToTimeZone(timestampMs, 'YYYY-MM-DD', {
    locale: locale || defaultLocale,
    timeZone: restaurantTimezone || defaultTimezone
  })
  const formattedTime = formatToTimeZone(timestampMs, 'HH:mm', {
    locale: locale || defaultLocale,
    timeZone: restaurantTimezone || defaultTimezone
  })

  return { date: formattedDate, time: formattedTime }
}
